import { formFieldMap } from '../components/fieldMap';

/** Configurable entity keys stored in tenantSettings.entityLabels */
export const ENTITY_LABEL_KEYS = ['commodity', 'account', 'location', 'laArea'];

/** Default user-facing labels for configurable entities. */
export const DEFAULT_ENTITY_LABELS = {
    commodity: { singular: 'Commodity', plural: 'Commodities' },
    account: { singular: 'Account', plural: 'Accounts' },
    location: { singular: 'Location', plural: 'Locations' },
    laArea: { singular: 'LA Area', plural: 'LA Areas' },
};

const ENTITY_TEXT_REPLACEMENTS = {
    commodity: [
        [/\bCommodities\b/g, 'plural'],
        [/\bcommodities\b/g, 'plural', true],
        [/\bCommodity\b/g, 'singular'],
        [/\bcommodity\b/g, 'singular', true],
    ],
    account: [
        [/\bAccounts\b/g, 'plural'],
        [/\baccounts\b/g, 'plural', true],
        [/\bAccount\b/g, 'singular'],
        [/\baccount\b/g, 'singular', true],
    ],
    location: [
        [/\bLocations\b/g, 'plural'],
        [/\blocations\b/g, 'plural', true],
        [/\bLocation\b/g, 'singular'],
        [/\blocation\b/g, 'singular', true],
    ],
    laArea: [
        [/\bLA Areas\b/g, 'plural'],
        [/\bla areas\b/g, 'plural', true],
        [/\bLA Area\b/g, 'singular'],
        [/\bla area\b/g, 'singular', true],
    ],
};

const ENTITY_FIELD_KEYS = {
    commodity: new Set(['commodityId', 'commodity', 'commodityGroup']),
    account: new Set(['accountId', 'account', 'assAccountId', 'accountType', 'accountOperation', 'accInvNo', 'accRunId']),
    location: new Set([
        'locationId', 'location', 'locationType', 'fromLocation', 'toLocation',
        'sourceLocationId', 'destinationLocationId',
    ]),
    laArea: new Set(['laAreaId', 'laArea']),
};

const ENTITY_LABEL_PATTERNS = {
    commodity: /\bCommodit(?:y|ies)\b/i,
    account: /\bAccounts?\b/i,
    location: /\bLocations?\b/i,
    laArea: /\bLA Areas?\b/i,
};

const LOOKUP_TYPE_LABELS = {
    commodityId: (labels) => labels.commodity.singular,
    accountId: (labels) => labels.account.singular,
    assAccountId: (labels) => `Master ${labels.account.singular}`,
    locationId: (labels) => labels.location.singular,
    sourceLocationId: (labels) => `Source ${labels.location.singular}`,
    destinationLocationId: (labels) => `Destination ${labels.location.singular}`,
    laAreaId: (labels) => labels.laArea.singular,
};

/**
 * Simple English pluralization for tenant-entered singular labels.
 * @param {string} singular
 */
export function pluralizeEntityLabel(singular) {
    const word = String(singular || '').trim();
    if (!word) return '';
    if (/[^aeiou]y$/i.test(word)) return `${word.slice(0, -1)}ies`;
    if (/(s|x|z|ch|sh)$/i.test(word)) return `${word}es`;
    return `${word}s`;
}

function resolveEntityLabel(tenantSettings, key) {
    const raw = tenantSettings?.entityLabels?.[key];
    const singular = typeof raw === 'string' ? raw.trim() : '';
    if (!singular) {
        return { ...DEFAULT_ENTITY_LABELS[key] };
    }
    return {
        singular,
        plural: pluralizeEntityLabel(singular),
    };
}

/**
 * @param {object|null|undefined} tenantSettings
 */
export function resolveEntityLabels(tenantSettings) {
    return ENTITY_LABEL_KEYS.reduce((acc, key) => {
        acc[key] = resolveEntityLabel(tenantSettings, key);
        return acc;
    }, {});
}

export function hasCustomEntityLabels(labels = DEFAULT_ENTITY_LABELS) {
    return ENTITY_LABEL_KEYS.some((key) => (
        labels[key].singular !== DEFAULT_ENTITY_LABELS[key].singular
        || labels[key].plural !== DEFAULT_ENTITY_LABELS[key].plural
    ));
}

/**
 * Replace default entity terminology in a user-facing string.
 * @param {string|null|undefined} text
 * @param {typeof DEFAULT_ENTITY_LABELS} [labels]
 */
export function applyEntityLabelText(text, labels = DEFAULT_ENTITY_LABELS) {
    if (text == null || typeof text !== 'string') return text;
    if (!hasCustomEntityLabels(labels)) return text;

    let result = text;
    for (const key of ENTITY_LABEL_KEYS) {
        const entity = labels[key] ?? DEFAULT_ENTITY_LABELS[key];
        if (
            entity.singular === DEFAULT_ENTITY_LABELS[key].singular
            && entity.plural === DEFAULT_ENTITY_LABELS[key].plural
        ) {
            continue;
        }

        for (const [pattern, form, lower] of ENTITY_TEXT_REPLACEMENTS[key]) {
            result = result.replace(pattern, () => {
                const value = form === 'plural' ? entity.plural : entity.singular;
                return lower ? value.toLowerCase() : value;
            });
        }
    }
    return result;
}

function shouldPatchFieldLabel(field) {
    if (!field) return false;
    for (const key of ENTITY_LABEL_KEYS) {
        if (ENTITY_FIELD_KEYS[key].has(field.key)) return true;
        if (ENTITY_LABEL_PATTERNS[key].test(field.label || '')) return true;
    }
    return false;
}

/**
 * @param {typeof DEFAULT_ENTITY_LABELS} labels
 */
export function getEffectiveFormFieldMap(labels = DEFAULT_ENTITY_LABELS) {
    if (!hasCustomEntityLabels(labels)) return formFieldMap;

    const result = {};
    for (const [formName, fields] of Object.entries(formFieldMap)) {
        result[formName] = fields.map((field) => (
            shouldPatchFieldLabel(field)
                ? { ...field, label: applyEntityLabelText(field.label, labels) }
                : field
        ));
    }
    return result;
}

/**
 * Apply tenant entity labels to lookup metadata.
 * @param {string} lookupType
 * @param {object} meta
 * @param {typeof DEFAULT_ENTITY_LABELS} [labels]
 */
export function patchLookupMetaLabel(lookupType, meta, labels = DEFAULT_ENTITY_LABELS) {
    const resolver = LOOKUP_TYPE_LABELS[lookupType];
    if (!resolver || !meta) return meta;
    return { ...meta, label: applyEntityLabelText(resolver(labels), labels) };
}

/**
 * @param {Array<{ label?: string }>} columns
 * @param {typeof DEFAULT_ENTITY_LABELS} [labels]
 */
export function applyEntityLabelsToColumns(columns, labels = DEFAULT_ENTITY_LABELS) {
    return (columns || []).map((col) => (
        col?.label
            ? { ...col, label: applyEntityLabelText(col.label, labels) }
            : col
    ));
}

/**
 * @param {Record<string, string>} labelMap
 * @param {typeof DEFAULT_ENTITY_LABELS} [labels]
 */
export function applyEntityLabelsToFieldLabelMap(labelMap, labels = DEFAULT_ENTITY_LABELS) {
    const result = {};
    for (const [key, value] of Object.entries(labelMap || {})) {
        result[key] = applyEntityLabelText(value, labels);
    }
    return result;
}

/** Nested lookup parent key -> entity label key */
export function getNestedParentLabel(parent, labels = DEFAULT_ENTITY_LABELS) {
    const resolved = labels ?? DEFAULT_ENTITY_LABELS;
    const map = {
        account: resolved.account.singular,
        assAccount: `Master ${resolved.account.singular}`,
        commodity: resolved.commodity.singular,
        location: resolved.location.singular,
        laArea: resolved.laArea.singular,
    };
    return map[parent] ?? null;
}
